import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { apiOkr } from 'app/config/axios-interceptor';
import { IKr, defaultValue } from 'app/shared/model/kr.model';
import { BaseResponseEntities, EntityState, IQueryParams, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';

const initialState: EntityState<IKr> = {
  loading: false,
  errorMessage: null,
  entities: [],
  options: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/okr/key-result';

// Actions

export const getEntitiesBox = createAsyncThunk(
  'key-result/fetch_entity_list',
  async ({ searchText, page, size, sort, filters }: IQueryParams) => {
    return apiOkr.get<any[]>(`${apiUrl}/select-box`, {
      params: {
        searchText,
        pageNum: page,
        pageSize: size,
        filters: filters && JSON.stringify(filters),
        sort: sort && sort,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getEntitiesCopy = createAsyncThunk(
  'key-result/fetch_entity_list_copy',
  async ({ searchText, planPeriodId, partyId, okrVersion }: any, { rejectWithValue }) => {
    return apiOkr
      .get<any[]>(`${apiUrl}/copy-list`, {
        params: {
          searchText,
          okrVersion,
          planPeriodId,
          partyId,
          cacheBuster: new Date().getTime(),
        },
      })
      .catch(err => {
        return rejectWithValue(err);
      });
  },
  { serializeError: serializeAxiosError },
);

interface IQueryKrParams extends IQueryParams {
  planPeriodId?: string;
  startDate?: string;
  endDate?: string;
  objectiveId?: string;
}

export const getEntitiesByYears = createAsyncThunk(
  'key-result/fetch_entity_list',
  async ({ searchText, page, size, sort, planPeriodId, endDate, startDate, objectiveId }: IQueryKrParams) => {
    return apiOkr.get<BaseResponseEntities<IKr>>(`${apiUrl}/by-year`, {
      params: {
        searchText,
        pageNum: page,
        pageSize: size,
        planPeriodId,
        endDate,
        startDate,
        objectiveId,
        sort: sort && sort,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getEntitiesByQuarter = createAsyncThunk(
  'key-result/fetch_entity_list',
  async ({ searchText, page, size, sort, planPeriodId, endDate, startDate, objectiveId }: IQueryKrParams) => {
    return apiOkr.get<BaseResponseEntities<IKr>>(`${apiUrl}/by-quarter`, {
      params: {
        searchText,
        pageNum: page,
        pageSize: size,
        planPeriodId,
        endDate,
        startDate,
        objectiveId,
        sort: sort && sort,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getEntitiesByMonth = createAsyncThunk(
  'key-result/fetch_entity_list',
  async ({ searchText, page, size, sort, planPeriodId, endDate, startDate, objectiveId }: IQueryKrParams) => {
    return apiOkr.get<BaseResponseEntities<IKr>>(`${apiUrl}/by-month`, {
      params: {
        searchText,
        pageNum: page,
        pageSize: size,
        planPeriodId,
        endDate,
        startDate,
        objectiveId,
        sort: sort && sort,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'key-result/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}?krId=${id}`;
    const result = await apiOkr.delete<IKr>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const getKrReportInfo = createAsyncThunk(
  'key-result-report/fetch_kr_report_info_list',
  async (id: string) => {
    return apiOkr.get<any>(`${apiUrl}/${id}/kr-reporting-info`);
  },
  { serializeError: serializeAxiosError },
);
// slice

export const KrSlice = createEntitySlice({
  name: 'key-result',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })

      .addMatcher(isFulfilled(getEntitiesBox), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          options: data,
        };
      })
      .addMatcher(isPending(getEntitiesBox), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
        state.options = [];
      })
      .addMatcher(isFulfilled(getEntitiesCopy), (state, action) => {
        const { data, headers } = action.payload;
        return {
          ...state,
          loading: false,
          entities: data,
        };
      })
      .addMatcher(isFulfilled(getEntitiesByYears, getEntitiesByMonth, getEntitiesByQuarter), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data.items,
          totalItems: data.totalRecord,
        };
      })
      .addMatcher(isPending(getEntitiesCopy), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(getEntitiesByYears, getEntitiesByMonth, getEntitiesByQuarter), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
        state.entities = [];
        state.entity = {};
      })
      .addMatcher(isPending(deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = KrSlice.actions;

// Reducer
export default KrSlice.reducer;
